import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Snackbar, Alert, CircularProgress,
  IconButton, InputAdornment, Checkbox, FormControlLabel, Chip,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginReq } from '../hooks/hooks';
import hero from '../assets/images/hero-dashboard-preview.webp';
import {
  Visibility, VisibilityOff, ArrowForward,
  Security, Speed, AccountBalance,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#F8FAFC',
    fontSize: 14,
    '& fieldset': { borderColor: '#E2E8F0', borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: '#94A3B8' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#0BA480', borderWidth: 2 },
      boxShadow: '0 0 0 3px rgba(11, 164, 128, 0.08)',
    },
    '&.Mui-error fieldset': { borderColor: '#EF4444' },
    transition: 'box-shadow 0.2s ease',
  },
  '& .MuiInputLabel-root': {
    fontSize: 14,
    fontWeight: 500,
    '&.Mui-focused': { color: '#0BA480' },
  },
  '& .MuiFormHelperText-root': { fontSize: 12 },
};

const FEATURES = [
  {
    icon: <Speed sx={{ fontSize: 18 }} />,
    title: 'Instant Transfers',
    desc: 'Move money in seconds across all networks',
  },
  {
    icon: <AccountBalance sx={{ fontSize: 18 }} />,
    title: 'Real-Time Analytics',
    desc: 'Track every transaction with live dashboards',
  },
  {
    icon: <Security sx={{ fontSize: 18 }} />,
    title: 'Bank-Grade Security',
    desc: '256-bit encryption with multi-factor authentication',
  },
];

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', duration: 5000 });

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login } = useAuth();

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema,
    onSubmit: (values) => mutation.mutate(values),
  });

  useEffect(() => {
    const remembered = localStorage.getItem('rememberedEmail');
    if (remembered) {
      formik.setFieldValue('email', remembered);
      setRememberMe(true);
    }
  }, []);

  const mutation = useMutation({
    mutationFn: LoginReq,
    onSuccess: (response) => {
      if (response?.user?.role !== 'business') {
        setSnackbar({
          open: true,
          message: (
            <span>
              This portal is for <strong>Business Accounts</strong> only. Download the{' '}
              <a href="https://play.google.com/store/apps/details?id=com.zenopay.user&hl=en" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>Android</a>
              {' '}or{' '}
              <a href="https://apps.apple.com/tz/app/zenopay/id6745407848" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>iOS</a>
              {' '}app, or contact <strong>support@zenopay.net</strong>.
            </span>
          ),
          severity: 'error',
          duration: 8000,
        });
        return;
      }

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formik.values.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      localStorage.setItem('userData', JSON.stringify({ ...response, loginTime: Date.now() }));
      login();
      queryClient.prefetchQuery({ queryKey: ['dashboard'], staleTime: 5 * 60 * 1000 });
      setSnackbar({ open: true, message: 'Login successful. Redirecting...', severity: 'success', duration: 3000 });
      const destination = response?.user?.kyc_status === 'approved' ? '/dashboard' : '/kyc-verification';
      setTimeout(() => navigate(destination), 800);
    },
    onError: (error) => {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      const data = error.response?.data;
      const message = data?.email || data?.password || data?.message || Object.values(data || {})[0] || 'Login failed. Please check your credentials.';
      setSnackbar({ open: true, message, severity: 'error', duration: 5000 });
    },
  });

  return (
    <Box display="flex" minHeight="100vh" sx={{ bgcolor: '#fff' }}>

      {/* ── Left: Form panel ── */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        px={{ xs: 3, sm: 6, lg: 10 }}
        py={6}
        sx={{ maxWidth: { md: '50%' } }}
      >


        <motion.div
          animate={shake ? { x: [-8, 8, -8, 8, -4, 4, 0] } : {}}
          transition={{ duration: 0.45 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <Chip
              label="Business Portal"
              size="small"
              sx={{
                mb: 2,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                bgcolor: 'rgba(11, 164, 128, 0.08)',
                color: '#0BA480',
                border: '1px solid rgba(11, 164, 128, 0.2)',
                borderRadius: '6px',
                height: 24,
              }}
            />

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ color: '#0F172A', letterSpacing: -0.5, mb: 1, fontSize: { xs: '1.75rem', md: '2rem' } }}
            >
              Sign in to your account
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 15, mb: 4, lineHeight: 1.6 }}>
              Manage payments, track transactions, and grow your business.
            </Typography>

            <form onSubmit={formik.handleSubmit} noValidate>
              <Box mb={2}>
                <TextField
                  label="Email address"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  fullWidth
                  autoComplete="email"
                  autoFocus
                  sx={inputSx}
                />
              </Box>

              <Box mb={1}>
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  fullWidth
                  autoComplete="current-password"
                  sx={inputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(p => !p)}
                          edge="end"
                          size="small"
                          sx={{ color: '#94A3B8', '&:hover': { color: '#0BA480' } }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5} mb={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      size="small"
                      sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#0BA480' } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 13, color: '#64748B' }}>Remember me</Typography>}
                />
                <Link
                  to="/forgot-password"
                  style={{ fontSize: 13, fontWeight: 600, color: '#0BA480', textDecoration: 'none' }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={mutation.isLoading}
                endIcon={!mutation.isLoading && <ArrowForward sx={{ fontSize: 18 }} />}
                sx={{
                  py: 1.6,
                  fontWeight: 600,
                  fontSize: 15,
                  borderRadius: '8px',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #0BA480 0%, #059669 100%)',
                  boxShadow: '0 2px 12px rgba(11, 164, 128, 0.3)',
                  letterSpacing: 0.1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 4px 18px rgba(11, 164, 128, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                  '&.Mui-disabled': { background: '#E2E8F0', color: '#94A3B8', boxShadow: 'none' },
                }}
              >
                {mutation.isLoading
                  ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Signing in...</>
                  : 'Sign In'}
              </Button>

              <Box mt={2} display="flex" alignItems="center" justifyContent="center" gap={0.75}>
                <Security sx={{ fontSize: 12, color: '#CBD5E1' }} />
                <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                  Protected by 256-bit SSL encryption
                </Typography>
              </Box>

              <Box mt={4} pt={3} sx={{ borderTop: '1px solid #F1F5F9', textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                  Don't have a business account?{' '}
                  <Link
                    to="https://zenopay.net/register"
                    style={{ color: '#0BA480', fontWeight: 600, textDecoration: 'none' }}
                  >
                    Create account
                  </Link>
                </Typography>
              </Box>
            </form>
          </motion.div>
        </motion.div>
      </Box>

      {/* ── Right: Brand panel ── */}
      <Box
        flex={1}
        display={{ xs: 'none', md: 'flex' }}
        flexDirection="column"
        justifyContent="center"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #0F172A 0%, #1E293B 55%, #0B2318 100%)',
          px: { md: 6, lg: 9 },
          py: 8,
        }}
      >
        {/* Dot grid */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
        }} />
        {/* Glow blobs */}
        <Box sx={{
          position: 'absolute', bottom: '5%', right: '-8%',
          width: 420, height: 420, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(11,164,128,0.2) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', top: '8%', left: '-4%',
          width: 280, height: 280, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(11,164,128,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 700, color: '#0BA480',
            textTransform: 'uppercase', letterSpacing: 2.5, mb: 3,
          }}>
            ZenoPay Business
          </Typography>

          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ color: '#F8FAFC', lineHeight: 1.15, mb: 2, fontSize: { md: '1.85rem', lg: '2.4rem' } }}
          >
            The Payment Platform Built for African Business
          </Typography>

          <Typography sx={{ color: '#94A3B8', fontSize: 15, mb: 5, lineHeight: 1.75, maxWidth: 400 }}>
            Collect, send, and manage money across mobile networks and banks — all from one powerful dashboard.
          </Typography>

          <Box display="flex" flexDirection="column" gap={3} mb={6}>
            {FEATURES.map(({ icon, title, desc }) => (
              <Box key={title} display="flex" alignItems="flex-start" gap={2}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
                  bgcolor: 'rgba(11,164,128,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#0BA480',
                }}>
                  {icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#F1F5F9', mb: 0.3 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                    {desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Dashboard preview */}
          <Box sx={{
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 32px 64px rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,255,255,0.06)',
            maxWidth: 500,
          }}>

          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 1 }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            fontWeight: 500,
            fontSize: 13,
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
